import React from "react";
import { motion } from "framer-motion";
import { Building2, Home, ArrowUpRight } from "lucide-react";

const COMMERCIAL_IMG =
  "https://static.prod-images.emergentagent.com/jobs/89f2f8d9-74f4-4335-b690-b9a815d801c5/images/29a394c6ebac1293f3df48f8711b0e12b2dbb9b14c818612a289626a5e4e2fbe.png";
const RESIDENTIAL_IMG =
  "https://images.pexels.com/photos/15798782/pexels-photo-15798782.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const BLOCKS = [
  {
    key: "commercial",
    eyebrow: "Commercial",
    icon: Building2,
    img: COMMERCIAL_IMG,
    title: "Offices, retail & hospitality.",
    desc: "Cat-A and Cat-B fit-outs, commercial refurbishments and out-of-hours strip-outs for landlords, agents and tenants across London.",
    points: ["Cat-A / Cat-B fit-out", "Out-of-hours programmes", "Dilapidations", "Working alongside PMs"],
  },
  {
    key: "residential",
    eyebrow: "Residential",
    icon: Home,
    img: RESIDENTIAL_IMG,
    title: "Homes, HMOs & rentals.",
    desc: "Full flat and house refurbishments, rental turnarounds and landlord packages — from single rooms to whole-property overhauls.",
    points: ["Full refurbishments", "Kitchens & bathrooms", "Rental turnarounds", "Landlord packages"],
  },
];

export default function CommercialResidential() {
  return (
    <section
      id="clients"
      data-testid="commercial-residential-section"
      className="bg-[#F5EFE5] text-[#2B2420] py-20 md:py-28"
    >
      <div className="px-6 md:px-10 lg:px-16 mb-12 md:mb-16 max-w-3xl">
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#B8732C] mb-5">
          <span className="brand-gold-divider" /> Who we work with
        </div>
        <h2
          className="font-black tracking-tighter leading-[0.95] text-4xl md:text-5xl lg:text-6xl"
          style={{ fontFamily: "Cabinet Grotesk" }}
        >
          Homeowners, landlords, offices <br /> and fellow contractors.
        </h2>
      </div>

      <div className="px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {BLOCKS.map((b, i) => {
          const Icon = b.icon;
          return (
            <motion.a
              href="#quote"
              key={b.key}
              data-testid={`sector-card-${b.key}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              className="group relative overflow-hidden bg-[#2B2420] text-white min-h-[480px] md:min-h-[560px] flex flex-col justify-end p-8 md:p-10"
            >
              <div
                className="absolute inset-0 bg-cover bg-center opacity-50 group-hover:opacity-65 transition-opacity duration-500 group-hover:scale-[1.03]"
                style={{ backgroundImage: `url(${b.img})` }}
                aria-hidden
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2B2420] via-[#2B2420]/80 to-[#2B2420]/25" aria-hidden />

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#C9A24C]">
                    <Icon className="w-4 h-4" />
                    {b.eyebrow}
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-white/70 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
                <h3
                  className="font-black tracking-tighter leading-[1.02] text-white text-3xl md:text-4xl lg:text-5xl"
                  style={{ fontFamily: "Cabinet Grotesk" }}
                >
                  {b.title}
                </h3>
                <p className="mt-5 text-white/80 max-w-md leading-relaxed text-sm md:text-base">
                  {b.desc}
                </p>
                <ul className="mt-6 flex flex-wrap gap-2">
                  {b.points.map((p) => (
                    <li
                      key={p}
                      className="text-[11px] uppercase tracking-[0.2em] border border-white/25 px-3 py-1.5"
                    >
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.a>
          );
        })}
      </div>
    </section>
  );
}
